import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { useCallback, useMemo } from "react";
import { GlobalStore, MakerToken, SelectedToken } from "store/global/GlobalStore";
import { Amount } from "../../types/amount";
import { getPriceForTokenByTokenList } from "../../utils/tokenListUtils";
import { CounterParty } from "utils/tradeUtils";
import { Tooltip } from "antd";
import { Text } from "components/general/Text";
import { InteractionStore } from "store/interactions/InteractionStore";
import { Hash } from "viem";
import { getTokenTicker, isAddressEqual } from "../../utils/tokenUtils";

interface AmountBarProps {
  counterParty: CounterParty;
  totalUsdAmount: Amount;
}

export const AmountBar: React.FC<AmountBarProps> = ({ counterParty, totalUsdAmount }) => {
  const tokens = GlobalStore.useState(s =>
    counterParty === "maker" ? s.makerTokens : s.takerTokens
  );
  const quote = GlobalStore.useState(s => s.quote);
  const tokenList = useTokenListByChain();
  const theme = GlobalStore.useState(s => s.theme);

  // hasAmounts mean different things for maker and taker side
  // Taker side: At least 1 of the trade leg has a populated amount in input
  // Maker side: A successful quote has been made, and each of the maker side
  // trade leg has an amount populated in GlobalStore
  const hasAmounts =
    counterParty === "maker"
      ? Object.values(quote?.makerTokens ?? {}).some(entry =>
          entry.amount.value.gt(Amount.ZERO.value)
        )
      : tokens.some(entry => entry.amount.value.gt(Amount.ZERO.value));

  const getWidth = useCallback(
    (token: SelectedToken) => {
      const tokenUsdAmount = token.amount.mul(
        getPriceForTokenByTokenList(token.tokenAddress, tokenList)
      );
      if (counterParty === "maker") {
        return (
          tokens.find(entry => isAddressEqual(token.tokenAddress, entry.tokenAddress)) as MakerToken
        ).pct;
      }
      return !hasAmounts
        ? 50
        : tokenUsdAmount.div(totalUsdAmount).mul(new Amount("100")).value.toString();
    },
    [tokenList, counterParty, tokens, totalUsdAmount, hasAmounts]
  );

  if (tokenList == null || tokens.length === 0) return null;

  const tokensToInclude =
    counterParty === "taker" && hasAmounts
      ? tokens.filter(token => token.amount.value.gt(0))
      : tokens;

  return (
    <Container isUnpopulated={!hasAmounts}>
      {tokensToInclude.map((entry, i, arr) => {
        const { colour } = tokenList[entry.tokenAddress] ?? {};
        const widthPct = getWidth(entry);

        return (
          <Tooltip
            key={i}
            title={<TooltipContent tokenAddress={entry.tokenAddress} />}
            color={theme.surface.main.secondary.hover}
            overlayInnerStyle={tooltipOverlayInnerStyle}
          >
            <TokenSegement
              color={colour ?? theme.font.primary.default}
              isFirst={i === 0}
              isLast={i === arr.length - 1}
              animate={{ width: `${widthPct}%` }}
              transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
              onClick={() => {
                InteractionStore.update(s => {
                  s.tradeLegToOpenOnAmountBarClick = entry.tokenAddress;
                });
              }}
            />
          </Tooltip>
        );
      })}
    </Container>
  );
};

// Sub-components

const Container = styled.div<{ isUnpopulated: boolean }>(({ isUnpopulated }) => ({
  width: "100%",
  height: 30,
  display: "flex",
  gap: 2,
  opacity: isUnpopulated ? 0.5 : 1,
}));

interface TokenSegementProps {
  color: string;
  isFirst: boolean;
  isLast: boolean;
}

const TokenSegement = styled(motion.button)(({ color, isFirst, isLast }: TokenSegementProps) => {
  const borderRadius = useMemo(() => {
    if (isFirst && isLast) return "13px";
    if (isFirst) {
      return "13px 0 0 13px";
    }
    if (isLast) {
      return "0 13px 13px 0";
    }
  }, [isFirst, isLast]);

  return {
    height: "100%",
    borderRadius,
    background: color,
    flexGrow: isLast ? 1 : 0,
  };
});

const TooltipContent = ({ tokenAddress }: { tokenAddress: Hash }) => {
  const tokenList = useTokenListByChain();

  if (tokenList == null) return null;

  return (
    <TooltipContentContainer>
      <TooltipIcon src={tokenList[tokenAddress]?.iconUrl} alt={`${tokenAddress} icon`} />
      <Text textStyle="Title" size="Small">
        {getTokenTicker(tokenAddress, tokenList)}
      </Text>
    </TooltipContentContainer>
  );
};

const TooltipContentContainer = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const TooltipIcon = styled.img`
  border-radius: 50%;
  border: 0.5px solid rgba(255, 255, 255, 0.4);
  width: 24px;
  height: 24px;
`;

// Helpers

const tooltipOverlayInnerStyle = {
  padding: "4px 10px 4px 4px",
  borderRadius: 114,
  boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.50), 0px 1px 15px 0px rgba(0, 0, 0, 0.25)",
};
